import { Paper, Stack, TextField, Typography } from '@mui/material';
import type { MissionLimits } from './workspaceApi';
export const runtimeBudgetReasonMessage = (error: unknown) => {
  const reason = (error as { data?: { reason?: unknown } })?.data?.reason;
  const messages: Record<string, string> = {
    unsupported_consumer: 'The selected transport cannot enforce these runtime budgets yet.',
    credit_unavailable:
      'Finite credit limits require native billing prices and reconciled credit evidence.',
    ownership_conflict: 'Another execution owns the campaign. Resolve its outstanding work first.',
    financial_review: 'Financial evidence requires reconciliation before new work can start.',
    dispatch_unknown: 'An earlier dispatch has an unknown outcome and needs review.',
  };
  return typeof reason === 'string' ? (messages[reason] ?? '') : '';
};
export const validMissionLimits = (v?: MissionLimits) =>
  !v ||
  ((v.credits == null || /^(?:0|[1-9][0-9]{0,37})(?:\.[0-9]{1,9})?$/.test(v.credits)) &&
    (v.tools == null || (Number.isSafeInteger(v.tools) && v.tools >= 0 && v.tools <= 1000000)) &&
    (v.seconds == null ||
      (Number.isSafeInteger(v.seconds) && v.seconds >= 1 && v.seconds <= 86400)));
export const MissionBudgetFields = ({
  value,
  onChange,
  disabled = false,
}: {
  value?: MissionLimits;
  onChange: (value: MissionLimits | undefined) => void;
  disabled?: boolean;
}) => {
  const set = (key: keyof MissionLimits, raw: string) => {
    const next = { ...value };
    if (!raw.trim()) delete next[key];
    else if (key === 'credits') next.credits = raw;
    else next[key] = Number(raw);
    onChange(Object.values(next).some((v) => v != null) ? next : undefined);
  };
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack gap={2}>
        <Typography variant="subtitle1">Run budgets</Typography>
        <Typography variant="body2" color="text.secondary">
          Optional limits apply to each published run. Exhaustion blocks new actions and calls;
          active calls finish, with overrun recorded in the original run. Empty fields keep existing
          unlimited behavior. Credit limits require native billing prices. Unsupported consumers are
          blocked by the server.
        </Typography>
        <TextField
          label="Credit limit per run"
          value={value?.credits ?? ''}
          disabled={disabled}
          error={value?.credits != null && !validMissionLimits({ credits: value.credits })}
          onChange={(e) => set('credits', e.target.value)}
          helperText="Exact decimal credits, up to 9 fractional digits. Zero blocks spending."
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
        />
        <TextField
          label="Tool invocations per run"
          type="number"
          value={value?.tools ?? ''}
          disabled={disabled}
          error={value?.tools != null && !validMissionLimits({ tools: value.tools })}
          onChange={(e) => set('tools', e.target.value)}
          slotProps={{ htmlInput: { min: 0, max: 1000000, step: 1 } }}
        />
        <TextField
          label="Run duration limit (seconds)"
          type="number"
          value={value?.seconds ?? ''}
          disabled={disabled}
          error={value?.seconds != null && !validMissionLimits({ seconds: value.seconds })}
          onChange={(e) => set('seconds', e.target.value)}
          slotProps={{ htmlInput: { min: 1, max: 86400, step: 1 } }}
        />
      </Stack>
    </Paper>
  );
};
